import React, { useState, useEffect } from 'react';
import clientCandidateDataService from '../../../services/clientCandidateDataService';
import SmsConsentManager from '../../SmsConsentManager';

/**
 * ContactsLinksWidget
 * Emails, Phones, LinkedIn profiles, Other Links - all with multiple entries
 * With easy copy-to-clipboard functionality
 * 
 * Email/Phone data shape: { value, type, preferred }
 * - type: 'Personal' | 'Work' | 'Other' | 'Do Not Contact' (emails)
 *         'Mobile' | 'Work' | 'Home' | 'Other' | 'Do Not Contact' (phones)
 * - preferred: boolean (indicates candidate's preferred contact method)
 */

const ContactsLinksWidget = ({ profile, candidateId, onRefresh }) => {
  const [activeForm, setActiveForm] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  // DNC state — owned here now that compliance toggles live alongside the
  // contact methods they govern. Hits the existing /api/outreach/dnc endpoint.
  const [dnc, setDnc] = useState({ dnc_sms: false, dnc_email: false, dnc_phone: false, dnc_all: false });

  useEffect(() => {
    if (!candidateId) return;
    fetch(`/api/outreach/dnc/${candidateId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
    })
      .then(r => (r.ok ? r.json() : {}))
      .then(d => setDnc(d || {}))
      .catch(() => {});
  }, [candidateId]);

  const toggleDNC = async (field) => {
    const newVal = !dnc[field];
    setDnc(prev => ({ ...prev, [field]: newVal }));
    try {
      await fetch(`/api/outreach/dnc/${candidateId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: newVal, reason: 'Manual' })
      });
    } catch (e) {
      setDnc(prev => ({ ...prev, [field]: !newVal }));
    }
  };

  // Compact DNC toggle row. Matches the simpler style from the original
  // tracking widget (no border, no background — just a clean toggle row).
  // Label rendered in red so it reads at-a-glance as a compliance warning.
  const DncSwatch = ({ field, label }) => {
    const active = !!dnc[field];
    const reason = dnc[`${field}_reason`];
    return (
      <div className="flex items-center justify-between py-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-red-600 font-medium">Do not contact via {label}</span>
          {active && reason && <span className="text-[9px] text-red-400">({reason})</span>}
        </div>
        <button
          type="button"
          onClick={() => toggleDNC(field)}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${active ? 'bg-red-500' : 'bg-gray-200'}`}
        >
          <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${active ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </button>
      </div>
    );
  };

  // Local mirror of clientData — updates instantly when entries are added,
  // edited, deleted, or promoted to preferred, without waiting for a parent
  // re-fetch. Synced from the prop on external updates.
  const [clientData, setClientData] = useState(profile?.client_data || {});
  useEffect(() => {
    setClientData(profile?.client_data || {});
  }, [profile?.client_data]);

  const emails = clientData.emails || [];
  const phones = clientData.phones || [];
  const linkedins = clientData.linkedins || [];
  const otherLinks = clientData.other_links || [];

  const emailTypes = ['Personal', 'Work', 'Other', 'Do Not Contact'];
  const phoneTypes = ['Mobile', 'Work', 'Home', 'Other', 'Do Not Contact'];

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const saveData = async (updates) => {
    const previous = clientData;
    const optimistic = { ...previous, ...updates };
    setClientData(optimistic);
    try {
      // Clean status is no longer reset here — marking a profile unclean is
      // reserved for tag changes only.
      await clientCandidateDataService.updateData(candidateId, { ...optimistic });
      // No onRefresh — the local mirror is authoritative for this widget.
    } catch (error) {
      console.error('Error saving:', error);
      setClientData(previous);
      alert('Failed to save');
    }
  };

  // Toggle the preferred flag on one entry. Mutually exclusive within a
  // section — promoting one demotes all others to keep "preferred" as a
  // single canonical contact per channel.
  const togglePreferred = async (section, index) => {
    const key = section === 'email' ? 'emails' : 'phones';
    const arr = (clientData[key] || []).map((item, i) => ({
      ...item,
      preferred: i === index ? true : false,
    }));
    await saveData({ [key]: arr });
  };

  const handleAdd = (section) => {
    setActiveForm(section);
    setEditingIndex(null);
    switch(section) {
      case 'email': setFormData({ value: '', type: 'Work', preferred: true }); break;
      case 'phone': setFormData({ value: '', type: 'Mobile', preferred: true }); break;
      case 'linkedin': setFormData({ url: '' }); break;
      case 'link': setFormData({ url: '', label: '' }); break;
      default: setFormData({});
    }
  };

  const handleEdit = (section, index, item) => {
    setActiveForm(section);
    setEditingIndex(index);
    setFormData({ ...item, preferred: item.preferred ?? false });
  };

  const handleCancel = () => { 
    setActiveForm(null); 
    setEditingIndex(null); 
    setFormData({}); 
  };

  const handleSave = async () => {
    let key, arr;
    switch(activeForm) {
      case 'email':
        if (!formData.value) { alert('Email is required'); return; }
        key = 'emails'; arr = [...emails]; break;
      case 'phone':
        if (!formData.value) { alert('Phone is required'); return; }
        key = 'phones'; arr = [...phones]; break;
      case 'linkedin':
        if (!formData.url) { alert('URL is required'); return; }
        key = 'linkedins'; arr = [...linkedins]; break;
      case 'link':
        if (!formData.url || !formData.label) { alert('URL and Label are required'); return; }
        key = 'other_links'; arr = [...otherLinks]; break;
      default: return;
    }
    if (editingIndex !== null) arr[editingIndex] = formData;
    else arr.push(formData);
    await saveData({ [key]: arr });
    handleCancel();
  };

  const handleDelete = async (section, index) => {
    if (!window.confirm('Delete this item?')) return;
    let key, arr;
    switch(section) {
      case 'email': key = 'emails'; arr = [...emails]; break;
      case 'phone': key = 'phones'; arr = [...phones]; break;
      case 'linkedin': key = 'linkedins'; arr = [...linkedins]; break;
      case 'link': key = 'other_links'; arr = [...otherLinks]; break;
      default: return;
    }
    arr.splice(index, 1);
    await saveData({ [key]: arr });
  };

  const getTypeColor = (type) => {
    if (type === 'Do Not Contact') return 'bg-red-100 text-red-700';
    if (type === 'Personal') return 'bg-purple-100 text-purple-700';
    if (type === 'Work') return 'bg-emerald-100 text-emerald-700';
    if (type === 'Mobile') return 'bg-green-100 text-green-700';
    if (type === 'Home') return 'bg-yellow-100 text-yellow-700';
    if (type === 'Other') return 'bg-gray-200 text-gray-700';
    return 'bg-gray-100 text-gray-700';
  };

  const renderForm = () => {
    if (!activeForm) return null;

    const bgColors = { email: '#eff6ff', phone: '#f0fdf4', linkedin: '#eff6ff', link: '#faf5ff' };
    const borderColors = { email: '#bfdbfe', phone: '#bbf7d0', linkedin: '#bfdbfe', link: '#e9d5ff' };
    const titleColors = { email: '#1e40af', phone: '#166534', linkedin: '#1e40af', link: '#6b21a8' };

    return (
      <div 
        className="rounded-lg p-3 mb-3" 
        style={{ backgroundColor: bgColors[activeForm], border: `1px solid ${borderColors[activeForm]}` }}
      >
        <h4 className="font-medium text-sm mb-2" style={{ color: titleColors[activeForm] }}>
          {editingIndex !== null ? 'Edit' : 'Add'} {
            activeForm === 'email' ? 'Email' : 
            activeForm === 'phone' ? 'Phone' : 
            activeForm === 'linkedin' ? 'LinkedIn' : 'Link'
          }
        </h4>

        {activeForm === 'email' && (
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Email *</label>
              <input 
                type="email" value={formData.value || ''} 
                onChange={(e) => setFormData({ ...formData, value: e.target.value })} 
                className="w-full px-2 py-1 text-sm border rounded" placeholder="email@example.com" 
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Type</label>
              <select 
                value={formData.type || 'Work'} 
                onChange={(e) => setFormData({ ...formData, type: e.target.value })} 
                className="w-full px-2 py-1 text-sm border rounded"
              >
                {emailTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox" checked={formData.preferred ?? false}
                onChange={(e) => setFormData({ ...formData, preferred: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-xs text-gray-700">Preferred contact</span>
              <svg className="w-3.5 h-3.5 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </label>
          </div>
        )}

        {activeForm === 'phone' && (
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Phone *</label>
              <input 
                type="tel" value={formData.value || ''} 
                onChange={(e) => setFormData({ ...formData, value: e.target.value })} 
                className="w-full px-2 py-1 text-sm border rounded" placeholder="+1 (555) 123-4567" 
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Type</label>
              <select 
                value={formData.type || 'Mobile'} 
                onChange={(e) => setFormData({ ...formData, type: e.target.value })} 
                className="w-full px-2 py-1 text-sm border rounded"
              >
                {phoneTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox" checked={formData.preferred ?? false}
                onChange={(e) => setFormData({ ...formData, preferred: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-xs text-gray-700">Preferred contact</span>
              <svg className="w-3.5 h-3.5 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </label>
          </div>
        )}

        {activeForm === 'linkedin' && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">LinkedIn URL *</label>
            <input 
              type="url" value={formData.url || ''} 
              onChange={(e) => setFormData({ ...formData, url: e.target.value })} 
              className="w-full px-2 py-1 text-sm border rounded" placeholder="https://linkedin.com/in/..." 
            />
          </div>
        )}

        {activeForm === 'link' && (
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Label *</label>
              <input 
                type="text" value={formData.label || ''} 
                onChange={(e) => setFormData({ ...formData, label: e.target.value })} 
                className="w-full px-2 py-1 text-sm border rounded" placeholder="e.g., Portfolio, GitHub" 
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">URL *</label>
              <input 
                type="url" value={formData.url || ''} 
                onChange={(e) => setFormData({ ...formData, url: e.target.value })} 
                className="w-full px-2 py-1 text-sm border rounded" placeholder="https://..." 
              />
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-3">
          <button onClick={handleSave} className="px-3 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700">
            {editingIndex !== null ? 'Update' : 'Add'}
          </button>
          <button onClick={handleCancel} className="px-3 py-1 border text-xs rounded hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    );
  };

  // Copy button — still used by LinkedIn and Other Links rows. Email/phone
  // rows now copy via clicking the value text directly.
  const CopyButton = ({ value, id }) => {
    const isCopied = copiedId === id;
    return (
      <button 
        onClick={(e) => { e.stopPropagation(); copyToClipboard(value, id); }}
        className={`p-1 transition-colors ${isCopied ? 'text-green-600' : 'text-gray-400 hover:text-emerald-700'}`}
        title={isCopied ? 'Copied!' : 'Copy to clipboard'}
      >
        {isCopied ? (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
    );
  };

  // Preferred star indicator
  const PreferredBadge = () => (
    <span className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 flex-shrink-0" title="Preferred">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
      </svg>
      Preferred
    </span>
  );

  // Contact item row (used for both email and phone)
  //  - Clicking the value text copies it to clipboard (no dedicated copy icon).
  //  - Preferred rows get a soft emerald tint (matches the clean-state header
  //    background) so the canonical contact is visually obvious.
  //  - A star button appears on hover left of edit/delete, only for entries
  //    that aren't already preferred — clicking it promotes that entry and
  //    demotes any other preferred entry in the same section.
  const ContactRow = ({ item, index, section, icon }) => {
    const rowId = `${section}-${index}`;
    const isCopied = copiedId === rowId;
    return (
      <div className={`flex items-center justify-between px-2 py-1.5 rounded group transition-colors ${item.preferred ? 'bg-emerald-50' : 'bg-gray-50'}`}>
        <div className="flex items-center gap-2 min-w-0">
          {icon}
          <button
            type="button"
            onClick={() => copyToClipboard(item.value, rowId)}
            title={isCopied ? 'Copied' : 'Click to copy'}
            className={`text-sm truncate text-left transition-colors cursor-pointer ${isCopied ? 'text-emerald-600' : 'text-gray-800 hover:text-emerald-700'}`}
          >
            {item.value}
          </button>
          {item.type && <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${getTypeColor(item.type)}`}>{item.type}</span>}
          {item.preferred && <PreferredBadge />}
        </div>
        <div className="opacity-0 group-hover:opacity-100 flex gap-0.5 flex-shrink-0">
          {!item.preferred && (
            <button
              onClick={() => togglePreferred(section, index)}
              title="Mark as preferred"
              className="p-1 text-gray-400 hover:text-amber-600"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.447a1 1 0 00-.363 1.118l1.286 3.957c.3.922-.755 1.688-1.54 1.118l-3.37-2.447a1 1 0 00-1.175 0l-3.37 2.447c-.784.57-1.838-.196-1.539-1.118l1.286-3.957a1 1 0 00-.364-1.118L2.98 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
              </svg>
            </button>
          )}
          <button onClick={() => handleEdit(section, index, item)} title="Edit" className="p-1 text-gray-400 hover:text-emerald-700">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button onClick={() => handleDelete(section, index)} title="Delete" className="p-1 text-gray-400 hover:text-red-600">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  const emailIcon = (
    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );

  const phoneIcon = (
    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Contacts & Links</h3>

      {/* Emails — faint red background when DNC for email is on, mirroring
          the emerald-on-preferred and amber-on-pending tints elsewhere so the
          warning state is visible at a glance. */}
      <div className={`mb-4 -mx-2 px-2 py-1 rounded transition-colors ${dnc.dnc_email ? 'bg-red-50' : ''}`}>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-600">Email Addresses</label>
          {activeForm !== 'email' && (
            <button onClick={() => handleAdd('email')} className="text-xs text-emerald-700 hover:text-emerald-700 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add
            </button>
          )}
        </div>
        {activeForm === 'email' && renderForm()}
        {emails.length === 0 && activeForm !== 'email' ? (
          <p className="text-sm text-gray-400 italic">None added</p>
        ) : (
          <div className="space-y-1">
            {emails.map((item, i) => (
              <ContactRow key={i} item={item} index={i} section="email" icon={emailIcon} />
            ))}
          </div>
        )}
        <DncSwatch field="dnc_email" label="email" />
      </div>

      <hr className="border-gray-200 my-3" />

      {/* Phones — faint red when either SMS or phone DNC is on. Either one
          makes the section partially risky to contact via that channel. */}
      <div className={`mb-4 -mx-2 px-2 py-1 rounded transition-colors ${(dnc.dnc_sms || dnc.dnc_phone) ? 'bg-red-50' : ''}`}>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-600">Phone Numbers</label>
          {activeForm !== 'phone' && (
            <button onClick={() => handleAdd('phone')} className="text-xs text-emerald-700 hover:text-emerald-700 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add
            </button>
          )}
        </div>
        {activeForm === 'phone' && renderForm()}
        {phones.length === 0 && activeForm !== 'phone' ? (
          <p className="text-sm text-gray-400 italic">None added</p>
        ) : (
          <div className="space-y-1">
            {phones.map((item, i) => (
              <ContactRow key={i} item={item} index={i} section="phone" icon={phoneIcon} />
            ))}
          </div>
        )}
        <DncSwatch field="dnc_sms" label="SMS" />
        <DncSwatch field="dnc_phone" label="phone" />
      </div>

      <hr className="border-gray-200 my-3" />

      {/* SMS Consent — A2P 10DLC opt-in capture, its own labeled section. */}
      <div className="mb-4">
        <label className="text-xs font-medium text-gray-600 mb-2 block">SMS Consent</label>
        <SmsConsentManager
          candidateId={candidateId}
          candidateName={profile?.name || profile?.first_name || 'Candidate'}
          roleTitle={null}
          onRefresh={onRefresh}
        />
      </div>

      <hr className="border-gray-200 my-3" />

      {/* LinkedIn */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-600">LinkedIn Profiles</label>
          {activeForm !== 'linkedin' && (
            <button onClick={() => handleAdd('linkedin')} className="text-xs text-emerald-700 hover:text-emerald-700 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add
            </button>
          )}
        </div>
        {activeForm === 'linkedin' && renderForm()}
        {linkedins.length === 0 && activeForm !== 'linkedin' ? (
          <p className="text-sm text-gray-400 italic">None added</p>
        ) : (
          <div className="space-y-1">
            {linkedins.map((item, i) => (
              <div key={i} className="flex items-center justify-between px-2 py-1.5 bg-gray-50 rounded group">
                <div className="flex items-center gap-2 min-w-0">
                  <svg className="w-4 h-4 text-emerald-700 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-sm text-emerald-700 hover:underline truncate">
                    {item.url.replace('https://www.linkedin.com/in/', '').replace('https://linkedin.com/in/', '').replace(/\/$/, '') || item.url}
                  </a>
                  <CopyButton value={item.url} id={`linkedin-${i}`} />
                </div>
                <div className="opacity-0 group-hover:opacity-100 flex gap-0.5 flex-shrink-0">
                  <button onClick={() => handleEdit('linkedin', i, item)} className="p-1 text-gray-400 hover:text-emerald-700">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button onClick={() => handleDelete('linkedin', i)} className="p-1 text-gray-400 hover:text-red-600">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <hr className="border-gray-200 my-3" />

      {/* Other Links */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-600">Other Links</label>
          {activeForm !== 'link' && (
            <button onClick={() => handleAdd('link')} className="text-xs text-emerald-700 hover:text-emerald-700 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add
            </button>
          )}
        </div>
        {activeForm === 'link' && renderForm()}
        {otherLinks.length === 0 && activeForm !== 'link' ? (
          <p className="text-sm text-gray-400 italic">None added</p>
        ) : (
          <div className="space-y-1">
            {otherLinks.map((item, i) => (
              <div key={i} className="flex items-center justify-between px-2 py-1.5 bg-gray-50 rounded group">
                <div className="flex items-center gap-2 min-w-0">
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  <span className="text-xs font-medium text-gray-600">{item.label}:</span>
                  <span className="text-sm text-gray-800 truncate">{item.url}</span>
                  <CopyButton value={item.url} id={`link-${i}`} />
                </div>
                <div className="opacity-0 group-hover:opacity-100 flex gap-0.5 flex-shrink-0">
                  <button onClick={() => handleEdit('link', i, item)} className="p-1 text-gray-400 hover:text-emerald-700">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button onClick={() => handleDelete('link', i)} className="p-1 text-gray-400 hover:text-red-600">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactsLinksWidget;
